import { MoreVert } from "@mui/icons-material";
import { Menu } from "@mui/material";
import React, { useEffect, useState } from "react";
import { OrganizationContent } from "../components";
import {
	ChangeMemberRoleModalContent,
	ChangeMemberRoleModalTitle,
	MemberAddButton,
	MemberAddButtonIcon,
	MemberMenuButton,
	MemberMenuItem,
	MembersCount,
	MembersOwnerChip,
	MembersOwnerText,
	MembersOwnerWrapper,
} from "./style";
import Table from "@/components/Table/Table";
import useCustomFormik from "@/hooks/formik/useCustomFormik";
import Modal from "@/components/Modal";
import Input from "@/components/Input";
import * as Yup from "yup";
import { UserResponseDto } from "@/api/interface";
import {
	deleteUser,
	getUserList,
	patchUserOwner,
	postUserInvite,
} from "@/views/Organization/Members/api";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading } from "@/redux/global/actions";
import getOrganizationInfoRequestThunk from "@/redux/organization/thunk";
import { getUserInfoRequestThunk } from "@/redux/user/thunk";
import { getUserInfo } from "@/redux/user/selector";
import { useTranslation } from "react-i18next";

interface InitialValues {
	addMemberEmail: string;
	deleteMemberText: string;
}

export default function OrganizationMembers() {
	const { t } = useTranslation();
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const open = Boolean(menuAnchor);
	const [menuData, setMenuData] = useState<null | UserResponseDto>(null);
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const [addMemberModal, setAddMemberModal] = useState(false);
	const [memberList, setMemberList] = useState<UserResponseDto[]>([]);
	const [deleteMemberId, setDeleteMemberId] = useState<number | null>(null);
	const [changeMemberRoleId, setChangeMemberRoleId] = useState<number | null>(null);
	const userData = useSelector(getUserInfo);

	const initialValues: InitialValues = {
		addMemberEmail: "",
		deleteMemberText: "",
	};

	const { getFieldProps, errors, values, setFieldValue } =
		useCustomFormik<InitialValues>({
			initialValues,
			validationSchema: Yup.object({
				addMemberEmail: Yup.string().email("올바른 이메일 형태를 입력해주세요."),
			}),
			onSubmit: () => {
				//
			},
		});

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		memberData: UserResponseDto,
	) => {
		setMenuData(memberData);
		setMenuAnchor(event.currentTarget);
	};
	const handleMenuClose = () => {
		setMenuAnchor(null);
		setMenuData(null);
	};

	const handleCloseAddMemberModal = () => {
		setAddMemberModal(false);
		setFieldValue("addMemberEmail", "");
	};

	const handleClickAddMemberModalSubmit = async () => {
		try {
			const data = await postUserInvite({ email: [values.addMemberEmail] });

			if (!data[0].isSend) {
				throw new Error("");
			}

			enqueueSnackbar(`${data[0].email}에게 메일이 정상적으로 발송되었습니다.`, {
				variant: "info",
			});
			handleCloseAddMemberModal();
		} catch (e) {
			enqueueSnackbar("멤버를 초대하지 못했습니다.", {
				variant: "error",
			});
		}
	};

	const handleClickDeleteMember = () => {
		if (!menuData) {
			return;
		}

		setDeleteMemberId(menuData.id);
	};

	const handleCloseDeleteMemberModal = () => {
		setDeleteMemberId(null);
		setFieldValue("deleteMemberText", "");
	};

	const handleClickDeleteMemberModalSubmit = async () => {
		if (!deleteMemberId) {
			return;
		}

		try {
			await deleteUser(deleteMemberId);

			enqueueSnackbar("멤버를 삭제했습니다.", {
				variant: "success",
			});
			handleCloseDeleteMemberModal();
			dispatch(getOrganizationInfoRequestThunk());
			setMenuAnchor(null);
			await fetchData();
		} catch (e) {
			enqueueSnackbar("멤버를 삭제하지 못했습니다.", {
				variant: "error",
			});
		}
	};

	const handleClickChangeMemberRole = () => {
		if (!menuData) {
			return;
		}
		setChangeMemberRoleId(menuData.id);
	};

	const handleClickChangeMemberRoleModalSubmit = async () => {
		if (!changeMemberRoleId) {
			return;
		}

		try {
			await patchUserOwner(changeMemberRoleId);

			enqueueSnackbar("권한을 변경했습니다.", {
				variant: "success",
			});
			setChangeMemberRoleId(null);
			dispatch(getOrganizationInfoRequestThunk());
			dispatch(getUserInfoRequestThunk());
			setMenuAnchor(null);
			await fetchData();
		} catch (e) {
			enqueueSnackbar("권한을 변경하지 못했습니다.", {
				variant: "error",
			});
		}
	};

	const fetchData = async () => {
		try {
			dispatch(setIsLoading(true));
			const data = await getUserList();

			setMemberList(data);
			dispatch(setIsLoading(false));
		} catch (e) {
			enqueueSnackbar("멤버 정보를 불러오지 못했습니다.", {
				variant: "error",
			});
			dispatch(setIsLoading(false));
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	return (
		<OrganizationContent
			title={t("organization_member.title")}
			actions={
				userData?.isOwner && (
					<MemberAddButton
						backgroundColor="#0071E3"
						onClick={() => setAddMemberModal(true)}
					>
						{t("organization_member.add_member")}
						<MemberAddButtonIcon />
					</MemberAddButton>
				)
			}
		>
			<Modal
				handleClose={() => setChangeMemberRoleId(null)}
				titleText="권한 위임"
				open={changeMemberRoleId !== null}
				submitButtonText="변경"
				cancelButtonText="취소"
				handleClickCancelButton={() => setChangeMemberRoleId(null)}
				handleClickSubmitButton={handleClickChangeMemberRoleModalSubmit}
			>
				<>
					<ChangeMemberRoleModalTitle>
						소유권을 이전하시겠습니까?
					</ChangeMemberRoleModalTitle>
					<ChangeMemberRoleModalContent>
						조직에는 소유자가 한 명만 있을 수 있으며 소유권을 전환하면
						자동으로 관리자로 지정됩니다. 어플리케이션을 삭제하고 회원을
						관리할 수 있는 권한을 잃게 됩니다.
					</ChangeMemberRoleModalContent>
				</>
			</Modal>
			<Modal
				handleClose={() => false}
				titleText={`${t("organization_member.add_member")}+`}
				open={addMemberModal}
				submitButtonText={t("organization_member.mail_invite")}
				cancelButtonText={t("organization_member.mail_cancel")}
				handleClickCancelButton={handleCloseAddMemberModal}
				disabledSubmitButton={Boolean(
					!values.addMemberEmail || errors.addMemberEmail,
				)}
				handleClickSubmitButton={handleClickAddMemberModalSubmit}
			>
				<Input
					titleText="이메일"
					placeholder="이메일을 정확히 입력해주세요"
					errorText={errors.addMemberEmail}
					{...getFieldProps("addMemberEmail")}
				/>
			</Modal>
			<Modal
				handleClose={() => false}
				titleText="멤버를 삭제 할까요?"
				open={Boolean(deleteMemberId)}
				submitButtonText="삭제"
				cancelButtonText="취소"
				handleClickCancelButton={handleCloseDeleteMemberModal}
				disabledSubmitButton={values.deleteMemberText !== "삭제"}
				handleClickSubmitButton={handleClickDeleteMemberModalSubmit}
			>
				<Input
					titleText="삭제를 입력해주세요"
					placeholder={`"삭제"를 입력해주세요`}
					{...getFieldProps("deleteMemberText")}
				/>
			</Modal>
			<MembersCount>
				{t("organization_member.member")}: {memberList.length}
			</MembersCount>

			<Table
				col={[20, 30, 45, 5]}
				headItems={[
					t("organization_member.name"),
					t("organization_member.mail"),
					t("organization_member.role"),
					"",
				]}
				rowItems={memberList.map((memberData) => [
					memberData.nameLast + memberData.nameFirst,
					memberData.email,
					<MembersOwnerWrapper>
						<MembersOwnerText>
							{memberData.isOwner ? "오너(Owner)" : "어드민(Admin)"}
						</MembersOwnerText>
						<MembersOwnerChip type={memberData.isOwner ? "owner" : "admin"}>
							Owner
						</MembersOwnerChip>
					</MembersOwnerWrapper>,
					userData?.isOwner && userData.id !== memberData.id && (
						<>
							<MemberMenuButton
								src={<MoreVert />}
								alt=""
								onClick={(e) => handleMenuClick(e, memberData)}
							/>
							<Menu
								open={open}
								anchorEl={menuAnchor}
								anchorOrigin={{
									horizontal: "right",
									vertical: "bottom",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: "right",
								}}
								onClose={handleMenuClose}
							>
								<MemberMenuItem onClick={handleClickChangeMemberRole}>
									역할 변경
								</MemberMenuItem>
								<MemberMenuItem onClick={handleClickDeleteMember}>
									삭제
								</MemberMenuItem>
							</Menu>
						</>
					),
				])}
			/>
		</OrganizationContent>
	);
}
